import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';

// Imported directly instead of via '../../components': TrustBadgesPanel is
// mounted inside LayoutSingleColumn, which most of the barrel file's exports
// pull in.
import IconCancel from '../IconCancel/IconCancel';
import TrustSeals from '../TrustSeals/TrustSeals';

import css from './TrustBadgesPanel.module.css';

const DISMISSED_STORAGE_KEY = 'toastablyTrustBadgesPanelDismissed';

/**
 * Floating trust row fixed in the viewport's bottom-right corner on every
 * page and viewport: the Gütesiegel beside the kundenversprechen.ch review
 * widget with its stars. The close chip reveals on hover/focus (always
 * visible on touch screens) and a dismissal is remembered across visits in
 * localStorage.
 *
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 */
const TrustBadgesPanel = props => {
  const { rootClassName, className } = props;
  const intl = useIntl();
  // SSR always renders the panel; a previous dismissal is only readable from
  // localStorage after mount.
  const [isDismissed, setIsDismissed] = useState(false);
  // The seal (a bundled 14KB image) arrives long before the third-party
  // widget, so the panel stays hidden until the iframe answers and then
  // fades in as one unit. If the provider never answers, no panel — better
  // than a seal beside a white box.
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  // The widget iframe is only rendered after mount: one that loads before hydration fires its load
  // event before React listens, and the panel would stay hidden.
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    try {
      if (window.localStorage.getItem(DISMISSED_STORAGE_KEY)) {
        setIsDismissed(true);
      }
    } catch (e) {
      // localStorage can be unavailable (e.g. blocked storage); show the panel.
    }
  }, []);

  const onDismiss = () => {
    setIsDismissed(true);
    try {
      window.localStorage.setItem(DISMISSED_STORAGE_KEY, '1');
    } catch (e) {
      // Without storage the dismissal just lasts for the current page view.
    }
  };

  if (isDismissed) {
    return null;
  }

  const classes = classNames(rootClassName || css.root, className, {
    [css.loading]: !isWidgetReady,
  });

  return (
    <div className={classes}>
      <TrustSeals
        showReviewWidget={mounted}
        className={css.seals}
        onWidgetLoad={() => setIsWidgetReady(true)}
      />
      <button
        className={css.closeButton}
        type="button"
        onClick={onDismiss}
        aria-label={intl.formatMessage({ id: 'TrustBadgesPanel.close' })}
      >
        <IconCancel />
      </button>
    </div>
  );
};

export default TrustBadgesPanel;
